/**
 * Roles: who can do what, and how a restaurant changes it.
 *
 * Two kinds of role share one list and must not read alike on screen.
 *
 * A **system role** — Owner, Manager, Waiter, Cashier, Kitchen — is a shared
 * row belonging to no tenant, identical on every restaurant on the platform.
 * It cannot be edited, and that is what makes it a system role rather than a
 * limitation of one: editing Manager here would edit it for everybody. The
 * server refuses with a message saying to copy it and edit the copy, and that
 * message is worth showing verbatim because it names the way forward.
 *
 * A **custom role** belongs to one restaurant and is whatever the five did not
 * cover: a bar supervisor who can void and comp but not open the safe, a
 * stocktaker who counts without seeing a price. Professional and above.
 *
 * `permissions` arrives with the list rather than behind a second call. A page
 * of role names with no permissions on it shows five words and no information,
 * and "what can a Cashier actually do" is the only question anybody opens it
 * to answer.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface Role {
  id: string;
  /** Goes in URLs and audit logs. Not the display name. */
  key: string;
  name: string;
  /** Shared with every restaurant on the platform, and not editable. */
  is_system: boolean;
  permissions: string[];
}

export interface Permission {
  key: string;
  /** The module that declared it — `orders`, `inventory`, `rbac`. */
  module: string;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['roles'] });
}

/**
 * Every role this restaurant can assign, system ones first.
 *
 * Available on every plan: a Starter site still has to be able to see what a
 * Waiter can do. Only *making* your own is Professional.
 */
export function useRoles() {
  const api = useApi();
  return useQuery({
    queryKey: ['roles', 'list'],
    queryFn: ({ signal }) => api.get<Role[]>('/v1/roles', signal),
    staleTime: 60_000,
  });
}

/**
 * Every permission that exists, for a role editor to offer.
 *
 * Comes from the module registry rather than a table, so it cannot offer a
 * switch wired to nothing — a permission exists exactly when some module
 * declares it. Group by `module` to build the editor; the keys are dotted
 * `module.thing.verb` and the module is already split out so two screens
 * cannot disagree about where one belongs.
 */
export function usePermissions() {
  const api = useApi();
  return useQuery({
    queryKey: ['roles', 'permissions'],
    queryFn: ({ signal }) => api.get<Permission[]>('/v1/permissions', signal),
    // The catalogue changes when the product ships a new module, not during a
    // service.
    staleTime: 30 * 60_000,
  });
}

/**
 * Make a role of this restaurant's own. Professional and above.
 *
 * `key` is lowercase letters, digits and underscores — it goes in URLs and
 * audit logs, so it is not the display name, and the server refuses a key that
 * looks like one. Generate it from the name by all means, but let it be
 * edited: two roles cannot share a key and the second attempt needs somewhere
 * to go.
 *
 * A permission that does not exist is refused by name. That check is worth
 * surfacing rather than swallowing: a role granting `orders.line.viod` looks
 * right on the screen that made it and refuses the waiter at the till.
 */
export function useCreateRole() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Role, ApiError, { key: string; name: string; permissions?: string[] }>({
    mutationFn: (input) =>
      api.post<Role>('/v1/roles', {
        key: input.key,
        name: input.name,
        permissions: input.permissions ?? [],
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Replace what a role grants. The whole set, never a diff.
 *
 * Send every permission the role should end up with, because that is how the
 * decision is actually made — somebody opens the role, ticks and unticks, and
 * saves. Sending only the changes would let two people editing at once merge
 * silently into a role neither of them designed.
 *
 * Refused on a system role with a message naming the way forward. Disable the
 * save on `is_system` rather than letting somebody fill in a form that cannot
 * be submitted.
 */
export function useSetRolePermissions() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Role, ApiError, { roleId: string; permissions: string[] }>({
    mutationFn: ({ roleId, permissions }) =>
      // The object, not a string: `request` serialises what it is given, and
      // a pre-stringified body was encoded twice and refused with a 422.
      api.request<Role>(`/v1/roles/${roleId}/permissions`, {
        method: 'PUT',
        body: { permissions },
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Permissions grouped by the module that declared them, module names sorted.
 *
 * Stated here rather than in a component so a role editor and an audit screen
 * cannot disagree about the grouping. The order within a module is the order
 * the server sent, which is alphabetical by key — related verbs on one noun
 * end up adjacent, which is how somebody ticking boxes reads them.
 */
export function permissionsByModule(permissions: Permission[]): [string, Permission[]][] {
  const groups = new Map<string, Permission[]>();
  for (const permission of permissions) {
    const existing = groups.get(permission.module);
    if (existing) existing.push(permission);
    else groups.set(permission.module, [permission]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * One role held by one person, at one site or at all of them.
 *
 * `location_id` null means every site — including any opened later, which is
 * the part worth saying out loud on a screen: somebody given Manager "at every
 * site" when there is one site becomes a manager of the second one the day it
 * opens.
 */
export interface RoleAssignment {
  id: string;
  role_id: string;
  role_key: string;
  role_name: string;
  location_id: string | null;
}

/** What somebody can do, as the roles they hold. Keyed by their login. */
export function useUserRoles(userId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['roles', 'assignments', userId ?? null],
    queryFn: ({ signal }) => api.get<RoleAssignment[]>(`/v1/users/${userId}/roles`, signal),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/**
 * Give somebody a role.
 *
 * Refused with a 403 naming the permission when the role carries anything the
 * person granting it does not hold — so a Manager handing out Owner is told
 * `*`. Check `missingToGrant` first and the refusal never has to happen at the
 * button.
 */
export function useAssignRole() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    RoleAssignment,
    ApiError,
    { userId: string; roleKey: string; locationId?: string }
  >({
    mutationFn: ({ userId, roleKey, locationId }) =>
      api.post<RoleAssignment>(`/v1/users/${userId}/roles`, {
        role_key: roleKey,
        location_id: locationId ?? null,
      }),
    onSettled: (_data, _error, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: ['roles', 'assignments', userId] });
    },
  });
}

/**
 * Take a role back. The same subset rule as giving one: nobody removes a role
 * they could not have granted, or a Manager could strip the Owner.
 */
export function useRevokeRole() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { userId: string; assignmentId: string }>({
    mutationFn: ({ userId, assignmentId }) =>
      api.request<void>(`/v1/users/${userId}/roles/${assignmentId}`, { method: 'DELETE' }),
    onSettled: (_data, _error, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: ['roles', 'assignments', userId] });
    },
  });
}

/**
 * The first permission a role carries that `held` does not — the reason the
 * server would refuse to let this person grant or revoke it — or null if they
 * can.
 *
 * Stated here rather than in a component so the screen that greys a role out
 * and the message that explains a 403 cannot disagree about the rule. `*` is
 * every permission: holding it means anything can be granted, and a role
 * carrying it can only be granted by somebody who holds it too.
 */
export function missingToGrant(
  role: Pick<Role, 'permissions'>,
  held: readonly string[],
): string | null {
  if (held.includes('*')) return null;
  if (role.permissions.includes('*')) return '*';
  return role.permissions.find((permission) => !held.includes(permission)) ?? null;
}
